import axios from "axios";
import type {
  School,
  LectureLength,
  Student,
  Teacher,
  Timeslot,
  TimeslotPreference,
} from "../types";

const API_BASE = "/api";

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Schools API
export const schoolsApi = {
  getAll: () => api.get<School[]>("/schools/"),
  get: (id: number) => api.get<School>(`/schools/${id}`),
  create: (data: { name: string; description?: string }) =>
    api.post<School>("/schools/", data),
  update: (id: number, data: { name: string; description?: string }) =>
    api.put<School>(`/schools/${id}`, data),
  delete: (id: number) => api.delete(`/schools/${id}`),
};

// Lecture Lengths API
export const lectureLengthsApi = {
  getBySchool: (schoolId: number) =>
    api.get<LectureLength[]>(`/lecture-lengths/school/${schoolId}`),
  create: (data: {
    school_id: number;
    duration: number;
    name: string;
    description?: string;
  }) => api.post<LectureLength>("/lecture-lengths/", data),
  update: (
    id: number,
    data: {
      school_id: number;
      duration: number;
      name: string;
      description?: string;
    },
  ) => api.put<LectureLength>(`/lecture-lengths/${id}`, data),
  delete: (id: number) => api.delete(`/lecture-lengths/${id}`),
};

// Students API
export const studentsApi = {
  getAll: () => api.get<Student[]>("/students/"),
  getBySchool: (schoolId: number) =>
    api.get<Student[]>(`/students/school/${schoolId}`),
  create: (data: {
    name: string;
    school_id: number;
    lecture_length_id?: number;
  }) => api.post<Student>("/students/", data),
  update: (
    id: number,
    data: { name: string; school_id: number; lecture_length_id?: number },
  ) => api.put<Student>(`/students/${id}`, data),
  delete: (id: number) => api.delete(`/students/${id}`),
};

// Teachers API
export const teachersApi = {
  getAll: () => api.get<Teacher[]>("/teachers/"),
  get: (id: number) => api.get<Teacher>(`/teachers/${id}`),
  create: (data: { name: string; email: string; max_session_length: number }) =>
    api.post<Teacher>("/teachers/", data),
  update: (
    id: number,
    data: { name: string; email: string; max_session_length: number },
  ) => api.put<Teacher>(`/teachers/${id}`, data),
  delete: (id: number) => api.delete(`/teachers/${id}`),
};

// Timeslots API
export const timeslotsApi = {
  getBySchool: (schoolId: number) =>
    api.get<Timeslot[]>(`/timeslots/school/${schoolId}`),
  create: (data: {
    day: string;
    start_time: string;
    duration: number;
    recurring: boolean;
    school_id: number;
  }) => api.post<Timeslot>("/timeslots/", data),
  delete: (id: number) => api.delete(`/timeslots/${id}`),
};

// Preferences API
export const preferencesApi = {
  getByStudent: (studentId: number) =>
    api.get<TimeslotPreference[]>(`/preferences/student/${studentId}`),
  create: (data: {
    student_id: number;
    day: string;
    start_time: string;
    duration: number;
    type: string;
    priority?: number;
  }) => api.post<TimeslotPreference>("/preferences/", data),
  delete: (id: number) => api.delete(`/preferences/${id}`),
};

// Assignments API
export interface AssignmentDTO {
  id?: number;
  student_id: number;
  timeslot_id: number;
  school_id: number;
}

export const assignmentsApi = {
  getBySchool: (schoolId: number) =>
    api.get<AssignmentDTO[]>(`/assignments/school/${schoolId}`),
  create: (data: {
    student_id: number;
    timeslot_id: number;
    school_id: number;
  }) => api.post<AssignmentDTO>("/assignments/", data),
  delete: (studentId: number, timeslotId: number) =>
    api.delete(`/assignments/student/${studentId}/timeslot/${timeslotId}`),
};

// Schedule API
export const scheduleApi = {
  compute: (schoolId: number) =>
    api.post<AssignmentDTO[]>(`/schedule/compute?school_id=${schoolId}`),
};

// Auth API
export const authApi = {
  me: () => api.get<{ id: number; name: string; email: string }>("/auth/me"),
  login: () => {
    window.location.href = "/api/auth/google/login";
  },
  logout: () => api.post("/auth/logout", {}),
};
